"use client";

import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

interface AboutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * AboutModal Component
 * Full clinic introduction — expands on the About section's inline copy.
 * Warm, medically credible tone. No founder namedrop (founders featured on /founders).
 */
export function AboutModal({ open, onOpenChange }: AboutModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[90vh] flex-col sm:max-w-[600px]"
        onOpenAutoFocus={(e) => e.preventDefault()}
        aria-describedby="about-description"
      >
        <DialogHeader>
          <div className="mb-2 flex flex-col gap-2">
            <Badge
              variant="outline"
              className="bg-primary/10 text-primary border-primary/20 w-fit"
            >
              About Us
            </Badge>
            <DialogTitle className="text-primary text-2xl font-bold">
              Welcome to SkinSio Clinics, Vadodara
            </DialogTitle>
            <DialogDescription
              id="about-description"
              className="text-foreground text-base font-medium"
            >
              Doctor-Led Medical Aesthetics — Where Science Meets Genuine Care
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="-mr-4 flex-1 overflow-y-auto pr-4">
          <div className="text-muted-foreground text-md space-y-4 pb-4 leading-relaxed">
            <p>
              At SkinSio Clinics, we believe healthy skin, confident hair, and
              graceful ageing should be guided by science — not trends. As a
              doctor-led medical aesthetic and skin cosmetics clinic in the heart
              of Vadodara, our philosophy is simple: every patient deserves
              evidence-based treatments, honest medical advice, and personalised
              care.
            </p>

            <p>
              Our multidisciplinary team brings together consultant
              dermatologists, aesthetic physicians, clinical cosmetologists, hair
              transplant surgeons, physiotherapists, occupational health
              consultants, and dental professionals under one roof. Whether you
              are seeking advanced laser treatments, acne and pigmentation
              solutions, hair restoration, anti-ageing procedures, facial
              aesthetics, or physiotherapy, your treatment is always delivered
              under medical supervision by qualified healthcare professionals.
            </p>

            <p>
              Every journey at SkinSio begins with a detailed medical
              consultation. We take the time to understand your concerns,
              evaluate your skin or hair scientifically, discuss your goals, and
              design an individualised treatment plan. No two patients are alike,
              which is why we never believe in one-size-fits-all solutions. Our
              commitment to ethical recommendations and personalised protocols is
              what distinguishes SkinSio from conventional beauty clinics.
            </p>

            <p>
              Our clinic features advanced US FDA-approved technologies for laser
              hair reduction, carbon laser facials, laser photofacials, skin
              rejuvenation, PRP therapies, regenerative biostimulators, body
              contouring, and modern facial aesthetic procedures. Every treatment
              is performed using strict medical-grade sterilisation and hygiene
              protocols, ensuring the highest standards of patient safety,
              comfort, and clinical excellence.
            </p>

            <p>
              Today, SkinSio Clinics is recognised as one of Vadodara&apos;s
              trusted destinations for laser hair removal, acne and acne scar
              management, pigmentation correction, anti-ageing treatments, bridal
              skincare, cosmetic injectables, hair restoration, and medical
              aesthetics. Patients from across Vadodara, Gujarat, and
              neighbouring states choose SkinSio for one reason — transparent
              medical care that delivers natural-looking, evidence-based results.
            </p>

            <p>
              <strong className="text-foreground">Visit SkinSio Clinics</strong>
              <br />
              A/203, The Atlantic, Makarpura Road, Near D-Mart, Vadodara,
              Gujarat – 390009.
            </p>

            <p className="text-foreground/80 italic">
              Experience medical aesthetics where science meets artistry,
              technology meets safety, and every treatment is centred around you.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
